<template>
    <!-- NOTE: Styling has been added for MOEA Admin messages. Instead of --external, add --admin to .message class. Can't see any fields that distinguish user levels in data -->

    <div v-if="thread">
        <!-- Page title(s) -->
        <h1 class="mb-2">
            Message
        </h1>
        <h2 class="h3 mb-5">
            {{ thread.subject }}
        </h2>

        <h2
            v-if="thread.property"
            class="h3 mb-5"
        >
            <span class="color-mid-grey">Property:</span> {{ thread.property.full_address }}
        </h2>

        <!-- Buttons -->
        <div class="d-flex justify-content-between">
            <div>
                <router-link
                    :to="{ name: 'messages' }"
                    class="btn btn--outline mb-3"
                >
                    Back
                </router-link>
            </div>
            <div>
                <div v-if="thread.property">
                    <router-link
                        :to="{ name: 'property', params: { id: thread.property.id } }"
                        class="btn mb-3"
                    >
                        View Property
                    </router-link>
                </div>
                <div>
                    <button
                        type="button"
                        class="btn btn--secondary"
                        @click="toggleReply"
                    >
                        Write Message
                    </button>
                </div>
            </div>
        </div>
        <!-- END Buttons -->

        <!-- New message -->
        <form
            v-if="showReply"
            id="message-form"
            ref="messageForm"
            class="mt-5"
            @submit.prevent="addReply(thread.id)"
        >
            <div class="field-wrap">
                <div class="field-wrap__input field-wrap__input">
                    <textarea
                        v-model="reply"
                        placeholder="Add reply here..."
                        rows="8"
                    />
                </div>
            </div>
            <button
                type="submit"
                class="btn btn--secondary"
            >
                Send Reply
            </button>
        </form>
        <!-- END New message -->

        <!-- Messages -->
        <div class="messages">
            <div
                v-for="message in thread.messages"
                :key="message.id"
                :class="{ 'message--external': message.user.id !== thread.creator.id }"
                class="message"
            >
                <div class="message__date">
                    <time>
                        <span>{{ message.created_at | moment('L') }}</span>
                        <span>{{ message.created_at | moment('HH:mm') }}</span>
                    </time>
                </div>
                <div class="message__inner">
                    <div v-html="message.body" />
                    <div class="d-flex justify-content-end">
                        <p class="mb-0 color-secondary text-bold">
                            {{ message.user.full_name }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <!-- END Messages -->
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import api from '../api';

export default {
    name: 'Message',
    data() {
        return {
            reply: '',
            showReply: false
        };
    },
    computed: {
        ...mapState('messages', ['thread'])
    },
    mounted() {
        this.getThread(this.$route.params.threadId);
    },
    methods: {
        ...mapActions('messages', ['clearThread', 'getThread']),
        addReply(threadId) {
            return api.post(`messages/${threadId}`, {
                message: this.reply
            })
                .then(() => {
                    this.$nextTick(() => {
                        this.reply = '';
                        this.toggleReply();
                        this.getThread(this.$route.params.threadId);
                    });
                });
        },
        toggleReply() {
            this.showReply = !this.showReply;
            if (this.showReply) {
                this.$nextTick(() => {
                    this.$scrollTo(this.$refs.messageForm);
                });
            }
        }
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.clearThread();
        });
    },
    beforeRouteUpdate(to, from, next) {
        this.getThread(to.params.page || 1)
            .then(() => {
                next();
            });
    }
};
</script>

<style lang="scss">
// messages
.messages {
    margin-top: 5rem;

    .message {
        margin-bottom: 3rem;

        @include media-breakpoint-up(lg) {
            &:last-child {
                &::before {
                    display: none;
                }
            }
        }
    }
}

// message
.message {
    position: relative;
    color: theme-color('dark-grey');

    @include media-breakpoint-up(lg) {
        padding-left: 115px;

        &__date {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
        }

        &::before {
            content: "";
            width: 2px;
            background: darken(theme-color('light-grey'), 5%);
            height: calc(100% + 3rem);
            position: absolute;
            left: 77px;
            top: 50%;
        }
    }

    &__inner {
        background: darken(white, 3%);
        padding: 3rem;
        border-radius: global-var('rounded-corners');
        position: relative;

        &::before {
            content: "";
            position: absolute;
            left: -10px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-top: 10px solid transparent;
            border-right: 10px solid darken(white, 3%);
            border-bottom: 10px solid transparent;
        }
    }

    &--external {
        .message__inner {
            background: white;

            &::before {
                border-right: 10px solid white;
            }
        }
    }

    &--admin {
        .message__date {
            time {
                &::after {
                    background: theme-color('primary');
                }
            }
        }

        .message__inner {
            background: white;
            border: 2px solid theme-color('primary');

            &::before {
                border: 0;
            }

            &::after {
                content: "Admin";
                color: theme-color('primary');
                position: absolute;
                right: 3rem;
                bottom: 1.7rem;
                font-size: 10px;
            }

            .color-secondary {
                color: theme-color('primary') !important;
            }
        }
    }

    time {
        display: block;
        position: relative;
        margin-bottom: 1rem;

        @include media-breakpoint-up(lg) {
            margin-bottom: 0;
            text-align: center;
            padding-right: 30px;
            width: 83.2px;
            text-align: center;
        }

        span {
            display: block;
            color: theme-color('mid-grey');
            font-weight: 600;
            @include font-size(1.2rem);

            &:last-of-type {
                color: theme-color('dark-grey');
            }
        }

        @include media-breakpoint-up(lg) {
            &::after {
                content: "";
                width: 10px;
                height: 10px;
                background: theme-color('secondary');
                border-radius: 50%;
                display: inline-block;
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
            }
        }
    }
}
</style>
